import { useState, useCallback, useMemo } from 'react';
import { Event, EventCreateRequest } from '@/api/bol';
import { getLogisticsObjectsApi } from '@/services/bolApiClient';
import { getLogisticsObjectIdKey, type LogisticsObjectEntity } from '@/types/logisticsObject';

export interface EventFilters {
  startAt: Date | null;
  endAt: Date | null;
  code: string;
  type: string;
}

export interface UseEventsReturn {
  events: Event[];
  loading: boolean;
  filters: EventFilters;
  appliedFilters: EventFilters;
  setFilters: (filters: EventFilters) => void;
  getEvents: (page?: number, pageSize?: number) => Promise<void>;
  createEvent: (eventData: EventCreateRequest, memberIdentifier: string) => Promise<void>;
  deleteEvent: (eventId: string, memberIdentifier: string) => Promise<void>;
  applyFilters: () => void;
  clearFilters: () => void;
  hasUnappliedChanges: () => boolean;
  total: number;
}

const InitialFilter: EventFilters = {
  startAt: null,
  endAt: null,
  code: '',
  type: '',
};

const buildFilterParams = (filters: EventFilters) => {
  const params: Record<string, unknown> = {};
  if (filters.startAt) params.startAt = filters.startAt;
  if (filters.endAt) params.endAt = filters.endAt;
  if (filters.code && filters.code.trim()) params.code = filters.code;
  if (filters.type && filters.type.trim()) params.type = filters.type;
  return params;
};

export const useEvents = (
  logisticsObjectType: LogisticsObjectEntity,
  objectId: string
): UseEventsReturn => {
  const api = useMemo(
    () => getLogisticsObjectsApi(logisticsObjectType),
    [logisticsObjectType],
  );
  const idKey = useMemo(
    () => getLogisticsObjectIdKey(logisticsObjectType),
    [logisticsObjectType],
  );
  const buildIdParams = useCallback(
    () => ({ [idKey]: objectId } as Record<string, string>),
    [idKey, objectId],
  );
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<EventFilters>(InitialFilter);
  const [appliedFilters, setAppliedFilters] = useState<EventFilters>(InitialFilter);
  const [total, setTotal] = useState(0);

  const hasUnappliedChanges = useCallback(
    () => JSON.stringify(filters) !== JSON.stringify(appliedFilters),
    [filters, appliedFilters]
  );

  const getEvents = useCallback(
    async (page = 0, pageSize = 25) => {
      setLoading(true);
      try {
        const offset = page * pageSize;
        const filterParams = buildFilterParams(appliedFilters);

        const response = await api.getLogisticsObjectEvents({
          ...buildIdParams(),
          limit: pageSize,
          offset,
          ...filterParams,
        });

        setEvents(response.results || []);
        setTotal(response.pagination?.total || 0);
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [api, appliedFilters, buildIdParams]
  );

  const createEvent = useCallback(
    async (eventData: EventCreateRequest, memberIdentifier: string) => {
      if (!memberIdentifier) {
        throw new Error('No member identifier available');
      }

      await api.createLogisticsObjectEvent({
        ...buildIdParams(),
        contentType: 'application/json' as const,
        eventCreateRequest: eventData,
        xBOLMemberIdentifier: memberIdentifier,
      });

      await getEvents();
    },
    [api, buildIdParams, getEvents]
  );

  const deleteEvent = useCallback(
    async (eventId: string, memberIdentifier: string) => {
      if (!memberIdentifier) {
        throw new Error('No member identifier available');
      }

      await api.deleteLogisticsObjectEventById({
        ...buildIdParams(),
        eventId,
        xBOLMemberIdentifier: memberIdentifier,
      });

      await getEvents();
    },
    [api, buildIdParams, getEvents]
  );

  const applyFilters = useCallback(() => {
    setAppliedFilters(filters);
  }, [filters]);

  const clearFilters = useCallback(() => {
    setFilters(InitialFilter);
    setAppliedFilters(InitialFilter);
  }, []);

  return {
    events,
    loading,
    filters,
    appliedFilters,
    setFilters,
    getEvents,
    createEvent,
    deleteEvent,
    applyFilters,
    clearFilters,
    hasUnappliedChanges,
    total,
  };
};
